import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { humanizeApiError } from "../api/client";
import {
  type DispatchTimelineResponse,
  type HeatmapResponse,
  type TimelineEvent,
  type TimelineSession,
  analyticsApi,
} from "../api/analytics";
import {
  type BitrixNotification,
  bitrixApi,
} from "../api/bitrix";
import { type Dispatch, dispatchesApi } from "../api/dispatches";
import { type Document, documentsApi } from "../api/documents";
import { type User, usersApi } from "../api/users";
import { useAuth } from "../auth/useAuth";
import { HeatmapPagesView } from "../components/HeatmapPagesView";
import { Breadcrumbs } from "../components/ui/breadcrumbs";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { ConfirmDialog } from "../components/ui/confirm-dialog";
import { Label } from "../components/ui/label";
import { Select } from "../components/ui/select";

const ACCESS_LEVEL_LABEL: Record<number, string> = {
  1: "1 — открытая ссылка",
  2: "2 — email-gate",
  3: "3 — одноразовый код",
};

const ACCESS_LEVEL_HINT: Record<number, string> = {
  1: "Уровень 1: открыть может любой, у кого есть ссылка. Без проверок.",
  2: "Уровень 2: перед просмотром получатель вводит email из списка менеджера. Cookie на 90 дней.",
  3: "Уровень 3: на email из списка отправляется одноразовый код (TTL 15 мин). Cookie на 90 дней.",
};

const EVENT_TYPE_LABELS: Record<string, string> = {
  session_start: "Сессия начата",
  session_end: "Сессия завершена",
  page_view: "Страница открыта",
  page_change: "Страница сменилась",
  click: "Клик",
  attention_tick: "Метка внимания",
  link_click: "Клик по ссылке",
  cta_click: "Клик по CTA",
  print_initiated: "Запрос печати",
  download_initiated: "Запрос скачивания",
  visibility_change: "Видимость изменена",
  scroll: "Скролл",
  selection: "Выделение текста",
};

function formatDate(iso: string | null): string {
  if (iso === null) return "—";
  return new Date(iso).toLocaleString("ru-RU", {
    dateStyle: "short",
    timeStyle: "medium",
  });
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m === 0 ? `${s} сек` : `${m} мин ${s} сек`;
}

function summarizePayload(event: TimelineEvent): string | null {
  if (event.payload === null) return null;
  const p = event.payload;
  switch (event.event_type) {
    case "click":
    case "link_click": {
      const x = typeof p.x === "number" ? p.x.toFixed(2) : null;
      const y = typeof p.y === "number" ? p.y.toFixed(2) : null;
      const url = typeof p.url === "string" ? p.url : null;
      const base = x !== null && y !== null ? `(${x}, ${y})` : null;
      return url !== null ? `${base ?? ""} ${url}`.trim() : base;
    }
    case "scroll": {
      const top = typeof p.scroll_top === "number" ? p.scroll_top : null;
      return top === null ? null : `scroll_top=${top.toFixed(2)}`;
    }
    case "visibility_change":
      return p.visible ? "visible" : "hidden";
    case "cta_click":
      return typeof p.variant === "string" ? p.variant : null;
    case "attention_tick": {
      const blocks = Array.isArray(p.blocks) ? p.blocks.length : 0;
      return `${blocks} блок(ов)`;
    }
    default:
      return null;
  }
}

function extractMessage(err: unknown): string {
  return humanizeApiError(err, { entityLabel: "Отправка" });
}

export function DispatchDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [dispatch, setDispatch] = useState<Dispatch | null>(null);
  const [document, setDocument] = useState<Document | null>(null);
  const [timeline, setTimeline] = useState<DispatchTimelineResponse | null>(null);
  const [heatmap, setHeatmap] = useState<HeatmapResponse | null>(null);
  const [uniqueSessions, setUniqueSessions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        // Сначала пробуем загрузить саму отправку: если её нет или путь
        // некорректен (например, в URL старый short_token, а API ждёт
        // UUID), нет смысла дёргать timeline/heatmap — они вернут ту же
        // 422/404 и засорят консоль.
        const d = await dispatchesApi.get(id!);
        if (cancelled) return;
        setDispatch(d);

        const [t, h] = await Promise.all([
          analyticsApi.dispatchTimeline(id!),
          analyticsApi.dispatchHeatmap(id!, {
            layer: "both",
            unique_sessions: uniqueSessions,
          }),
        ]);
        if (cancelled) return;
        setTimeline(t);
        setHeatmap(h);

        // Документ нужен для pages_count в heatmap-сетке. Грузим отдельно,
        // чтобы не блокировать первичную картину карточки на отдельный
        // запрос — если он упал, остальные блоки всё равно отрисуются.
        try {
          const doc = await documentsApi.get(d.document_id);
          if (!cancelled) setDocument(doc);
        } catch {
          /* heatmap всё равно покажется без сетки страниц */
        }
      } catch (err) {
        if (!cancelled) setError(extractMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [id, uniqueSessions]);

  if (!id) {
    return <div className="text-muted-foreground">Не указан id отправки.</div>;
  }

  if (error) {
    return (
      <div className="space-y-4">
        <BackToDispatches />
        <Breadcrumbs
          items={[
            { label: "Отправки", to: "/dispatches" },
            { label: "Отправка" },
          ]}
        />
        <Card>
          <CardContent className="text-red-700 py-3 text-sm">{error}</CardContent>
        </Card>
      </div>
    );
  }

  if (loading && dispatch === null) {
    return <div className="text-muted-foreground">Загрузка…</div>;
  }

  if (dispatch === null || timeline === null) {
    return null;
  }

  return (
    <div className="space-y-4">
      <BackToDispatches />
      <Breadcrumbs
        items={[
          { label: "Отправки", to: "/dispatches" },
          { label: dispatch.short_token },
        ]}
      />

      <DispatchHeaderCard
        dispatch={dispatch}
        canReassign={user?.role === "admin"}
        onUpdated={(updated) => setDispatch(updated)}
      />

      <BitrixStatusCard dispatchId={dispatch.id} />

      <SessionsCard sessions={timeline.sessions} />

      {heatmap && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-medium">
              Тепловая карта (по отправке)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {document === null ? (
              <div className="text-sm text-muted-foreground">
                Загрузка…
              </div>
            ) : (
              <HeatmapPagesView
                heatmap={heatmap}
                pagesCount={document.pages_count ?? 0}
                uniqueSessions={uniqueSessions}
                onUniqueSessionsChange={setUniqueSessions}
              />
            )}
          </CardContent>
        </Card>
      )}

      <TimelineCard
        sessions={timeline.sessions}
        truncated={timeline.truncated_events}
      />
    </div>
  );
}

function BackToDispatches() {
  const navigate = useNavigate();
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() => navigate("/dispatches")}
      data-testid="back-to-dispatches"
    >
      <span aria-hidden="true" className="mr-1">←</span>К отправкам
    </Button>
  );
}

type LifecycleMode = "idle" | "extend" | "reassign";

function DispatchHeaderCard({
  dispatch,
  canReassign,
  onUpdated,
}: {
  dispatch: Dispatch;
  canReassign: boolean;
  onUpdated: (d: Dispatch) => void;
}) {
  const [mode, setMode] = useState<LifecycleMode>("idle");
  const [busy, setBusy] = useState<null | "disable" | "enable">(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [disableConfirmOpen, setDisableConfirmOpen] = useState(false);

  async function runToggle(kind: "disable" | "enable") {
    setBusy(kind);
    setActionError(null);
    try {
      const res =
        kind === "disable"
          ? await dispatchesApi.disable(dispatch.id)
          : await dispatchesApi.enable(dispatch.id);
      onUpdated(res);
    } catch (err) {
      setActionError(extractMessage(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card>
      <CardHeader className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle className="text-base font-medium">
            Отправка <span className="font-mono">{dispatch.short_token}</span>
          </CardTitle>
          {dispatch.is_disabled ? (
            <span className="inline-block rounded px-2 py-0.5 text-xs bg-red-50 text-red-700">
              Отключена
            </span>
          ) : (
            <span className="inline-block rounded px-2 py-0.5 text-xs bg-emerald-50 text-emerald-700">
              Активна
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {dispatch.is_disabled ? (
            <Button
              type="button"
              variant="default"
              size="sm"
              disabled={busy !== null}
              onClick={() => void runToggle("enable")}
              data-testid="btn-enable"
            >
              {busy === "enable" ? "Загрузка…" : "Включить"}
            </Button>
          ) : (
            <Button
              type="button"
              variant="destructive"
              size="sm"
              disabled={busy !== null}
              onClick={() => setDisableConfirmOpen(true)}
              data-testid="btn-disable"
            >
              {busy === "disable" ? "Загрузка…" : "Отключить"}
            </Button>
          )}
          {mode !== "extend" && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setMode("extend")}
              data-testid="btn-extend-toggle"
            >
              Продлить срок
            </Button>
          )}
          {canReassign && mode !== "reassign" && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setMode("reassign")}
              data-testid="btn-reassign-toggle"
            >
              Сменить менеджера
            </Button>
          )}
        </div>
        {actionError && (
          <div className="text-sm text-red-700" role="alert">
            {actionError}
          </div>
        )}
        {mode === "extend" && (
          <ExtendForm
            dispatch={dispatch}
            onCancel={() => setMode("idle")}
            onApplied={(d) => {
              onUpdated(d);
              setMode("idle");
            }}
          />
        )}
        {mode === "reassign" && canReassign && (
          <ReassignForm
            dispatch={dispatch}
            onCancel={() => setMode("idle")}
            onApplied={(d) => {
              onUpdated(d);
              setMode("idle");
            }}
          />
        )}
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <dt className="text-muted-foreground">Получатель</dt>
          <dd>
            {dispatch.recipient_email ?? (
              <span className="text-muted-foreground">не указан</span>
            )}
          </dd>
          <dt className="text-muted-foreground">Документ</dt>
          <dd>
            <Link
              to={`/documents/${dispatch.document_id}`}
              className="text-blue-700 hover:underline"
              title={dispatch.document_id}
            >
              {dispatch.document_title ?? (
                <span className="font-mono">
                  {dispatch.document_id.slice(0, 8)}…
                </span>
              )}
            </Link>
          </dd>
          <dt className="text-muted-foreground">Менеджер</dt>
          <dd>
            {dispatch.manager_email ?? (
              <span
                className="font-mono text-muted-foreground"
                title={dispatch.manager_user_id}
              >
                {dispatch.manager_user_id.slice(0, 8)}…
              </span>
            )}
          </dd>
          <dt className="text-muted-foreground">Уровень доступа</dt>
          <dd title={ACCESS_LEVEL_HINT[dispatch.access_level] ?? undefined}>
            {ACCESS_LEVEL_LABEL[dispatch.access_level] ?? dispatch.access_level}
          </dd>
          <dt className="text-muted-foreground">Истекает</dt>
          <dd>{formatDate(dispatch.expires_at)}</dd>
          <dt className="text-muted-foreground">Создана</dt>
          <dd>{formatDate(dispatch.created_at)}</dd>
          <dt className="text-muted-foreground">Ссылка для viewer'а</dt>
          <dd className="flex flex-wrap items-center gap-2">
            <a
              className="text-blue-700 hover:underline break-all"
              href={dispatch.viewer_url}
              target="_blank"
              rel="noreferrer noopener"
            >
              {dispatch.viewer_url}
            </a>
            <CopyButton value={dispatch.viewer_url} />
          </dd>
        </dl>
      </CardContent>
      <ConfirmDialog
        open={disableConfirmOpen}
        title="Отключить отправку?"
        description="Получатель потеряет доступ к документу. Действие можно отменить кнопкой «Включить»."
        confirmLabel="Отключить"
        busy={busy === "disable"}
        onConfirm={() => {
          setDisableConfirmOpen(false);
          void runToggle("disable");
        }}
        onCancel={() => setDisableConfirmOpen(false)}
      />
    </Card>
  );
}

function ExtendForm({
  dispatch,
  onCancel,
  onApplied,
}: {
  dispatch: Dispatch;
  onCancel: () => void;
  onApplied: (d: Dispatch) => void;
}) {
  // Пресеты по PRD §6.4 — 7/30/90/без срока. Кастомное число дней
  // не запрашиваем (этого нет в ТЗ, и админу проще промахнуться рукой).
  const [choice, setChoice] = useState<"7" | "30" | "90" | "none">("30");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const ttlDays = choice === "none" ? null : Number(choice);
      const res = await dispatchesApi.extend(dispatch.id, ttlDays);
      onApplied(res);
    } catch (err) {
      setError(extractMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded border p-3 space-y-2 bg-muted/40">
      <Label className="text-sm">Продлить на:</Label>
      <div className="flex flex-wrap gap-2 text-sm">
        {(["7", "30", "90", "none"] as const).map((v) => (
          <label key={v} className="flex items-center gap-1.5">
            <input
              type="radio"
              name="extend-ttl"
              value={v}
              checked={choice === v}
              onChange={() => setChoice(v)}
              data-testid={`extend-ttl-${v}`}
            />
            {v === "none" ? "Бессрочно" : `${v} дней`}
          </label>
        ))}
      </div>
      {error && <div className="text-sm text-red-700">{error}</div>}
      <div className="flex gap-2">
        <Button
          type="button"
          size="sm"
          disabled={busy}
          onClick={() => void submit()}
          data-testid="btn-extend-submit"
        >
          {busy ? "Загрузка…" : "Применить"}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={onCancel}
        >
          Отменить
        </Button>
      </div>
    </div>
  );
}

function ReassignForm({
  dispatch,
  onCancel,
  onApplied,
}: {
  dispatch: Dispatch;
  onCancel: () => void;
  onApplied: (d: Dispatch) => void;
}) {
  // Список менеджеров грузим лениво — карточка отправки часто открывается
  // без намерения переназначать, и тратить запрос на /users впустую не
  // хочется.
  const [users, setUsers] = useState<User[] | null>(null);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [target, setTarget] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoadingUsers(true);
      try {
        // role=manager: PRD §10.1 — отправками владеют менеджеры.
        // Админов не показываем, чтобы случайно не запутать назначение.
        const res = await usersApi.list({ size: 100, is_active: true });
        if (cancelled) return;
        const managers = res.items.filter(
          (u) => u.role === "manager" && u.id !== dispatch.manager_user_id,
        );
        setUsers(managers);
      } catch (err) {
        if (!cancelled) setError(extractMessage(err));
      } finally {
        if (!cancelled) setLoadingUsers(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [dispatch.manager_user_id]);

  async function submit() {
    if (!target) return;
    setBusy(true);
    setError(null);
    try {
      const res = await dispatchesApi.reassignManager(dispatch.id, target);
      onApplied(res);
    } catch (err) {
      setError(extractMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded border p-3 space-y-2 bg-muted/40">
      <Label htmlFor="reassign-target" className="text-sm">
        Новый менеджер
      </Label>
      {loadingUsers ? (
        <div className="text-sm text-muted-foreground">Загрузка…</div>
      ) : users && users.length === 0 ? (
        <div className="text-sm text-muted-foreground">
          Нет других активных менеджеров
        </div>
      ) : (
        <Select
          id="reassign-target"
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          data-testid="reassign-target"
        >
          <option value="">Выберите…</option>
          {(users ?? []).map((u) => (
            <option key={u.id} value={u.id}>
              {u.full_name} ({u.email})
            </option>
          ))}
        </Select>
      )}
      {error && <div className="text-sm text-red-700">{error}</div>}
      <div className="flex gap-2">
        <Button
          type="button"
          size="sm"
          disabled={busy || !target}
          onClick={() => void submit()}
          data-testid="btn-reassign-submit"
        >
          {busy ? "Загрузка…" : "Назначить"}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={onCancel}
        >
          Отменить
        </Button>
      </div>
    </div>
  );
}

const BITRIX_STATUS_LABEL: Record<BitrixNotification["status"], string> = {
  queued: "В очереди",
  sending: "Отправляется",
  sent: "Отправлено",
  failed: "Ошибка",
  dlq: "Не отправлено",
};

const BITRIX_STATUS_BADGE: Record<BitrixNotification["status"], string> = {
  queued: "bg-amber-50 text-amber-700",
  sending: "bg-blue-50 text-blue-700",
  sent: "bg-emerald-50 text-emerald-700",
  failed: "bg-red-50 text-red-700",
  dlq: "bg-red-100 text-red-800",
};

function BitrixStatusCard({ dispatchId }: { dispatchId: string }) {
  const [items, setItems] = useState<BitrixNotification[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const reload = useCallback(async () => {
    setError(null);
    try {
      const res = await bitrixApi.listNotifications({
        dispatch_id: dispatchId,
        size: 20,
      });
      setItems(res.items);
    } catch (err) {
      setError(extractMessage(err));
    }
  }, [dispatchId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // При непустом списке карточка раскрыта по умолчанию (там есть события,
  // которые админ хочет видеть). При пустом — collapsed, чтобы не
  // занимать место (B5: типичный случай — уведомлений ещё нет).
  const isEmpty = items !== null && items.length === 0;
  const open = expanded || !isEmpty;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base font-medium">
          Уведомления в Bitrix
          {items !== null && (
            <span className="ml-2 text-xs text-muted-foreground">
              ({items.length})
            </span>
          )}
        </CardTitle>
        {isEmpty && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
            aria-expanded={open}
          >
            {open ? "Свернуть" : "Развернуть"}
          </button>
        )}
      </CardHeader>
      {open && (
        <CardContent>
          {error && <div className="text-sm text-red-700 mb-2">{error}</div>}
          {items === null ? (
            <div className="text-sm text-muted-foreground">Загрузка…</div>
          ) : items.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              По этой отправке уведомлений ещё нет — порог не достигнут или
              у менеджера не задан bitrix_user_id
            </div>
          ) : (
            <ol className="space-y-2 text-sm">
              {items.map((n) => {
                const retryable = n.status === "dlq" || n.status === "failed";
                return (
                  <li
                    key={n.id}
                    className="flex flex-col gap-1 rounded border p-2"
                    data-testid={`bitrix-card-${n.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={`inline-block rounded px-2 py-0.5 text-xs ${BITRIX_STATUS_BADGE[n.status]}`}
                      >
                        {BITRIX_STATUS_LABEL[n.status]}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {n.notification_kind === "hot_lead"
                          ? "Горячий лид"
                          : "Истечение"}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        попыток: {n.attempt_count}
                      </span>
                      <span className="ml-auto text-xs text-muted-foreground">
                        {n.sent_at
                          ? `отправлено ${formatDate(n.sent_at)}`
                          : n.dlq_at
                            ? `не отправлено ${formatDate(n.dlq_at)}`
                            : `план ${formatDate(n.execute_at)}`}
                      </span>
                      {retryable && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={retryingId === n.id}
                          onClick={() => onRetryHandler(n.id)}
                          data-testid={`bitrix-card-retry-${n.id}`}
                        >
                          Повторить
                        </Button>
                      )}
                    </div>
                    {n.last_error && (
                      <div className="text-xs text-muted-foreground">
                        Ошибка: {n.last_error}
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </CardContent>
      )}
    </Card>
  );

  async function onRetryHandler(notificationId: string) {
    setRetryingId(notificationId);
    try {
      await bitrixApi.retry(notificationId);
      await reload();
    } catch (err) {
      setError(extractMessage(err));
    } finally {
      setRetryingId(null);
    }
  }
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  async function onClick() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Старые браузеры / отозванное разрешение — не падаем, просто
      // оставляем ссылку выделяемой текстом.
    }
  }

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() => void onClick()}
      data-testid="btn-copy-viewer-url"
    >
      {copied ? "Скопировано" : "Скопировать"}
    </Button>
  );
}

// Назначаем устройствам человечные метки «Устройство A/B/…» по первому
// появлению. UUID/fingerprint остаётся в title, чтобы при наведении было видно
// техническое значение. Это сразу делает очевидным, открывал ли получатель
// КП с одного устройства или с разных — UUID-сноски это скрывали.
function buildDeviceLabels(
  sessions: TimelineSession[],
): Record<string, string> {
  const labels: Record<string, string> = {};
  let i = 0;
  // Идём в обратном порядке: timeline в коде сортирован «новые сверху»,
  // а буква «A» естественнее для первого устройства в истории.
  for (let n = sessions.length - 1; n >= 0; n--) {
    const s = sessions[n];
    if (!s) continue;
    if (labels[s.device_uuid] === undefined) {
      labels[s.device_uuid] = letterLabel(i);
      i++;
    }
  }
  return labels;
}

function letterLabel(index: number): string {
  // 0..25 → A..Z; 26..  → AA, AB, …
  if (index < 26) return String.fromCharCode(65 + index);
  const hi = Math.floor(index / 26) - 1;
  const lo = index % 26;
  return String.fromCharCode(65 + hi) + String.fromCharCode(65 + lo);
}

function SessionsCard({ sessions }: { sessions: TimelineSession[] }) {
  const deviceLabels = useMemo(() => buildDeviceLabels(sessions), [sessions]);

  if (sessions.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium">Сессии получателя</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Получатель ещё не открывал ссылку
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-medium">
          Сессии получателя ({sessions.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-muted-foreground">
            <tr className="border-b">
              <th className="text-left font-normal px-4 py-2">Открыта</th>
              <th className="text-left font-normal px-4 py-2">Завершена</th>
              <th
                className="text-left font-normal px-4 py-2"
                title="Время, в течение которого вкладка была активна и получатель взаимодействовал с документом"
              >
                Активно
              </th>
              <th
                className="text-left font-normal px-4 py-2"
                title="Сколько страниц получатель прочитал (страница считается засчитанной после доскролла и удержания)"
              >
                Засчитано
              </th>
              <th
                className="text-left font-normal px-4 py-2"
                title="На какой странице получатель закрыл документ"
              >
                Выход
              </th>
              <th
                className="text-left font-normal px-4 py-2"
                title="Терминальные действия: клик по CTA, попытка печати, попытка скачивания"
              >
                Действия
              </th>
              <th
                className="text-left font-normal px-4 py-2"
                title="Идентификатор устройства (fingerprint). Ярлык «Новое устройство» — если получатель открыл с устройства, которое мы раньше не видели"
              >
                Устройство
              </th>
            </tr>
          </thead>
          <tbody>
            {sessions.map((s) => (
              <tr key={s.id} className="border-b last:border-b-0">
                <td className="px-4 py-2 text-muted-foreground">
                  {formatDate(s.started_at)}
                </td>
                <td className="px-4 py-2 text-muted-foreground">
                  {formatDate(s.ended_at)}
                </td>
                <td className="px-4 py-2">
                  {formatDuration(s.total_active_seconds)}
                </td>
                <td className="px-4 py-2">
                  <span title={s.counted_pages.join(", ")}>
                    {s.counted_pages.length}
                  </span>
                </td>
                <td className="px-4 py-2 text-muted-foreground">
                  {s.exit_page !== null ? `стр. ${s.exit_page}` : "—"}
                </td>
                <td className="px-4 py-2">
                  <SessionActionBadges session={s} />
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-2">
                    <span
                      className="text-sm"
                      title={`Идентификатор устройства: ${s.device_uuid}`}
                      data-testid={`device-label-${s.id}`}
                    >
                      Устройство {deviceLabels[s.device_uuid]}
                    </span>
                    {s.is_new_device && (
                      <span className="rounded px-1.5 py-0.5 text-xs bg-amber-50 text-amber-700">
                        Новое
                      </span>
                    )}
                    {s.partial_match && (
                      <span className="rounded px-1.5 py-0.5 text-xs bg-amber-50 text-amber-700">
                        Похожий fingerprint
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function SessionActionBadges({ session }: { session: TimelineSession }) {
  // PRD §11.10 — терминальные отметки сессии: CTA-клик, инициирована
  // печать, скачивание. Показываем компактные бейджи с tooltip-таймстампом
  // только для тех действий, что реально были — пустая ячейка означает
  // «ничего из этого не произошло».
  const items: { key: string; label: string; ts: string | null }[] = [
    { key: "cta", label: "CTA", ts: session.cta_clicked_at },
    { key: "print", label: "Печать", ts: session.print_initiated_at },
    { key: "download", label: "Скачивание", ts: session.downloaded_at },
  ];
  const triggered = items.filter((it) => it.ts !== null);
  if (triggered.length === 0) {
    return <span className="text-muted-foreground">—</span>;
  }
  return (
    <div className="flex flex-wrap items-center gap-1">
      {triggered.map((it) => (
        <span
          key={it.key}
          title={`${it.label}: ${formatDate(it.ts)}`}
          data-testid={`session-action-${it.key}`}
          className="rounded px-1.5 py-0.5 text-xs bg-emerald-50 text-emerald-700"
        >
          {it.label}
        </span>
      ))}
    </div>
  );
}


type DisplayRow =
  | {
      kind: "event";
      key: string;
      event: TimelineEvent & { sessionId: string };
    }
  | {
      kind: "attention_group";
      key: string;
      from: string;
      to: string;
      count: number;
      pageNumber: number | null;
    };

function TimelineCard({
  sessions,
  truncated,
}: {
  sessions: TimelineSession[];
  truncated: boolean;
}) {
  // attention_tick прилетают каждую секунду, пока вкладка активна — на
  // длинном просмотре они визуально топят полезные события (клики,
  // скроллы, page_change). Подряд идущие тики на одной странице
  // схлопываем в одну строку-агрегат с диапазоном времени и счётчиком.
  const rows = useMemo<DisplayRow[]>(() => {
    const all = sessions.flatMap((s) =>
      s.events.map((e) => ({ ...e, sessionId: s.id })),
    );
    all.sort((a, b) => a.client_ts.localeCompare(b.client_ts));

    const out: DisplayRow[] = [];
    all.forEach((e, i) => {
      if (e.event_type !== "attention_tick") {
        out.push({ kind: "event", key: `${e.sessionId}-${i}`, event: e });
        return;
      }
      const last = out[out.length - 1];
      if (
        last !== undefined &&
        last.kind === "attention_group" &&
        last.pageNumber === e.page_number
      ) {
        last.to = e.client_ts;
        last.count += 1;
        return;
      }
      out.push({
        kind: "attention_group",
        key: `${e.sessionId}-${i}`,
        from: e.client_ts,
        to: e.client_ts,
        count: 1,
        pageNumber: e.page_number,
      });
    });
    return out;
  }, [sessions]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-medium">
          События
          {truncated && (
            <span className="ml-2 text-xs text-amber-700">
              (часть событий не вошла — показаны первые)
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <div className="text-sm text-muted-foreground">Событий ещё нет</div>
        ) : (
          <ol className="space-y-1 text-sm font-mono">
            {rows.map((row) =>
              row.kind === "event" ? (
                <li
                  key={row.key}
                  className="grid grid-cols-[12rem_10rem_8rem_1fr] gap-2"
                >
                  <span className="text-muted-foreground">
                    {formatDate(row.event.client_ts)}
                  </span>
                  <span>
                    {EVENT_TYPE_LABELS[row.event.event_type] ??
                      row.event.event_type}
                  </span>
                  <span className="text-muted-foreground">
                    {row.event.page_number !== null
                      ? `стр. ${row.event.page_number}`
                      : ""}
                  </span>
                  <span className="text-muted-foreground">
                    {summarizePayload(row.event) ?? ""}
                  </span>
                </li>
              ) : (
                <li
                  key={row.key}
                  className="grid grid-cols-[12rem_10rem_8rem_1fr] gap-2"
                  title={`${formatDate(row.from)} → ${formatDate(row.to)}`}
                >
                  <span className="text-muted-foreground">
                    {formatDate(row.from)}
                  </span>
                  <span>
                    Внимание ×{row.count}
                  </span>
                  <span className="text-muted-foreground">
                    {row.pageNumber !== null ? `стр. ${row.pageNumber}` : ""}
                  </span>
                  <span className="text-muted-foreground">
                    {row.count === 1
                      ? "1 секунда"
                      : `${row.count} секунд подряд`}
                  </span>
                </li>
              ),
            )}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}
